
// --- Vue & Template imports ---
import { Component, Vue} from 'vue-property-decorator';
import DownloadExcel from 'vue-json-excel/JsonExcel.vue';

// --- Models ---
import { Driver, DriverRoute, DriverType } from '@/models/Driver.model';
import { DriverPayment } from '@/models/DriverPayment.model';
import { LoadEvent, LoadStop } from '../models/Load.model';

// --- Services ---
import AppService from '@/services/app';
import DriverPaymentService from '@/services/driverPayment';
import DriverRouteService from '@/services/driverRoute';
import DriverService from '@/services/driver';
import DriverTypeService from '@/services/driverType';
import LoadEventService from '@/services/loadEvent';
import LoadService from '@/services/load';
import VehicleService from '@/services/vehicle';

// --- Third Party ---

@Component({
  components: {   
    DownloadExcel,
  },
})
export default class DriverPaymentListing extends Vue {

  // used by Filter Area
  search: string = '';
  filterOptions: string[] = ['Pending', 'Open Batch'];
  paymentFilter: string = 'Pending';

  isBulkDelete: boolean = false;
  selectedPayments: DriverPayment[] = [];

  // used by No Data Slot
  isLoading: boolean = true;
  displayPayments: DriverPayment[] = [];

  expandDelay: boolean = false;
  loadStops: LoadStop[] = [];

  // used by Item Dialog for Create/Update
  showItemDialog: boolean = false;
  title: string = '';
  clickedItem: DriverPayment = {
    id: 0,
    loadId: 0,
    loadDate: '',
    horseVehicleId: 0,
    horseVehicleCode: '',
    trailer1VehicleId: 0,
    trailer1VehicleCode: '',
    driverTypeId: 0,
    driverType: '',
    driverId: 0,
    driverNickname: '',
    from: '',
    to: '',
    rate: 0,
    distance: 0,
    totalM3: 0,
    value: 0,
    linkBonus: false,
    isPrimary: true,
    criticalComment: '',
  };

  selectedDriver: Driver = Object.assign({}, this.$store.getters['drivers/emptyDriver']);

  valid: boolean = false;
  clicked: boolean = false;

  rules = {
    required: (value: string) => !!value || '',
    numbersOnly: (value: string) => (/^\d+$/.test(value)) || 'Invalid character',
  };

  // used by Load Event Dialog
  showLoadEventDialog: boolean = false;
  loadEventDialogTitle: string = '';
  loadEvents: LoadEvent[] = [];
  commentFormValid: boolean = false;
  newComment: string = '';
  addCommentClicked: boolean = false;

  // used by Confirm Action Dialog for Delete/Approve Item
  action: string = '';
  dialogContent: string = '';
  confirmActionDelay: boolean = false;
  showConfirmActionDialog: boolean = false;

  // #region Functions to display list data on page load
    headers() {
      if (this.paymentFilter === 'Pending') {
        return [
          {
            text: 'Load#',
            value: 'loadId',
          },
          {
            text: 'Date',
            value: 'loadDate',
          },
          {
            text: 'Type',
            value: 'driverType',
          },
          {
            text: 'Driver',
            value: 'driverNickname',
          },
          {
            text: 'From',
            value: 'from',
          },
          {
            text: 'To',
            value: 'to',
          },
          {
            text: 'Distance',
            value: 'distance',
          },
          {
            text: 'Bonus',
            value: 'linkBonus',
          },
          /*
          // Why are we showing this about the LOAD on the Driver Payments screen??
          {
            sortable: false,
            text: '',
            value: 'criticalComment',
          },
          */
          {
            sortable: false,
            text: '',
            value: 'action',
            align: 'end',
          },
        ];
      } else {
        return [
          {
            text: 'Load#',
            value: 'loadId',
          },
          {
            text: 'Date',
            value: 'loadDate',
          },
          {
            text: 'Type',
            value: 'driverType',
          },
          {
            text: 'Driver',
            value: 'driverNickname',
          },
          {
            text: 'From',
            value: 'from',
          },
          {
            text: 'To',
            value: 'to',
          },
          {
            text: 'Distance',
            value: 'distance',
          },
          {
            text: 'Bonus',
            value: 'linkBonus',
          },
          /*
          // Why are we showing this about the LOAD on the Driver Payments screen??
          {
            sortable: false,
            text: '',
            value: 'criticalComment',
          },
          */
          {
            text: 'Approved',
            value: 'approved',
          },
          {
            sortable: false,
            text: '',
            value: 'action',
            align: 'end',
          },
        ];
      }
    }

    offloadHeaders() {
      return [
        {
          text: 'Client',
          value: 'client',
        },
        {
          text: 'Address',
          value: 'address',
        },
        {
          text: 'Appointment Time',
          value: 'appointmentTime',
        },
        {
          text: 'Arrived',
          value: 'arrivedAt',
        },
      ];
    }

    async beforeMount() {
      // update filter list:
      if (this.restrictedFilterFunctionality()) {
        this.filterOptions = ['Pending'];
      }

      await AppService.listStatuses(false);
      await VehicleService.listTypes(false);
      await DriverService.listActive(true);
      await DriverTypeService.listAll(true);
      await DriverRouteService.listAll(true);
      await this.listPending(true);

      this.filterPayments();
    }

    async listPending(refreshFromAPI: boolean) {
      try {
        this.isLoading = true;
        const response: string = await DriverPaymentService.listPending(refreshFromAPI);

        if (response.length > 0 && response !== 'No Data Found') {
          AppService.errorHandler(response);
        }

      } finally {
        this.isLoading = false;
      }
    }

    async listApproved(refreshFromAPI: boolean) {
      try {
        this.isLoading = true;
        const response: string = await DriverPaymentService.listApproved(refreshFromAPI);

        if (response.length > 0 && response !== 'No Data Found') {
          AppService.errorHandler(response);
        }

      } finally {
        this.isLoading = false;
      }
    }

    async filterPayments() {
      switch (this.paymentFilter) {
        case ('Pending'):
          await this.listPending(false);
          this.displayPayments = this.$store.getters['driverPayments/pendingPayments'];
          this.selectedPayments = [];
          break;

        case ('Open Batch'):
          await this.listApproved(false);
          this.displayPayments = this.$store.getters['driverPayments/approvedPayments'];
          this.selectedPayments = [];
          break;
      }
    }

    async listOffloads(item: DriverPayment) {
      try {
        const response = await LoadService.listOffloadsByLoad({ params: { loadId: item.loadId }});

        if (response && response.data) {
          if (response.data.result && response.data.result === 'false') {
            AppService.errorHandler(response.data.message);
            this.loadStops = [];
          } else {
            this.loadStops = response.data;
          }
        } else {
          // response is undefined or has no data field - SHOULD NEVER HAPPEN!
          throw new Error('response: ' + JSON.stringify(response));
        }

      } catch (error) {

        if (error.response) {
          AppService.errorHandler(error.response.statusText);
        } else {
          AppService.logSupportDebug('Search_Loads.vue - listOffloads - 1189 - ' + error);
          AppService.errorHandler(this.$store.getters['app/messages'].couldNotConnect);
        }
      }
    }

    async expandPayment(item: DriverPayment, expand: any) {
      this.loadStops = [];

      if (item.offloadCount && item.offloadCount > 0) {
        // go fetch the load stops for this load
        try {
          this.expandDelay = true;
          await this.listOffloads(item);
        } finally {
          this.expandDelay = false;
        }
      }

      expand(true);
    }

    closeRow(expand: any) {
      expand(false);
    }

    restrictedFilterFunctionality() {
      return this.$store.getters['app/allowAccess'](this.$store.getters['users/loggedInUser'], [
        { type: 'Administrator', level: 2},
        { type: 'Operator', level: 2},
      ]);
    }

    allowAddCommentFunctionality() {
      return this.$store.getters['app/allowAccess'](this.$store.getters['users/loggedInUser'], [
        { type: 'Super User', level: 2},
        { type: 'Administrator', level: 2},
        { type: 'Operator', level: 2},
      ]);
    }

    allowAddNewFunctionality() {
      return this.$store.getters['app/allowAccess'](this.$store.getters['users/loggedInUser'], [
        { type: 'Super User', level: 2},
        { type: 'Operator', level: 3},
      ]);
    }

    allowEditFunctionality() {
      return this.$store.getters['app/allowAccess'](this.$store.getters['users/loggedInUser'], [
        { type: 'Super User', level: 2},
        { type: 'Operator', level: 3},
      ]);
    }

    allowApproveFunctionality() {
      return this.$store.getters['app/allowAccess'](this.$store.getters['users/loggedInUser'], [
        { type: 'Super User', level: 2},
        { type: 'Administrator', level: 2},
        { type: 'Operator', level: 2},
      ]);
    }

    allowFinaliseFunctionality() {
      return this.$store.getters['app/allowAccess'](this.$store.getters['users/loggedInUser'], [
        { type: 'Super User', level: 2},
      ]);
    }

    allowDeleteFunctionality() {
      return this.$store.getters['app/allowAccess'](this.$store.getters['users/loggedInUser'], [
        { type: 'Super User', level: 2},
        { type: 'Operator', level: 3},
      ]);
    }
  // #endregion

  // #region Functions required for the Item Dialog for Create/Update
    selectDriver() {
      this.clickedItem.linkBonus = false;
      this.clickedItem.rate = 0;

      // populate rate:
      const index = this.$store.getters['drivers/driverTypes'].findIndex((item: DriverType) => item.id === this.selectedDriver.typeId)

      if (index >= 0) {
        this.clickedItem.driverType = this.$store.getters['drivers/driverTypes'][index].type;
        this.clickedItem.rate = this.$store.getters['drivers/driverTypes'][index].rate;

        if (!this.clickedItem.rate) {
          this.clickedItem.rate = 0;
        }
      }

      // set link bonus
      if (this.selectedDriver.linkBonus && this.clickedItem.trailer2VehicleId && (this.clickedItem.trailer1VehicleId !== this.clickedItem.trailer2VehicleId)) {
        this.clickedItem.linkBonus = true;
      }
    }

    hasRouteKms() {
      const index = this.$store.getters['drivers/driverRoutes'].findIndex((item: DriverRoute) => (item.from.toUpperCase() === this.clickedItem.from.toUpperCase()) && (item.to.toUpperCase() === this.clickedItem.to.toUpperCase()));
      if (index >= 0) {
        return true;
      } else {
        return false;
      }
    }

    useRouteKms() {
      const index = this.$store.getters['drivers/driverRoutes'].findIndex((item: DriverRoute) => (item.from.toUpperCase() === this.clickedItem.from.toUpperCase()) && (item.to.toUpperCase() === this.clickedItem.to.toUpperCase()));
      if (index >= 0) {
        this.clickedItem.distance = this.$store.getters['drivers/driverRoutes'][index].km;
      }
    }

    async launchNewItemDialog(item: DriverPayment) {
      this.clickedItem = {
        id: 0,
        loadId: item.loadId,
        loadDate: item.loadDate,
        horseVehicleId: item.horseVehicleId,
        horseVehicleCode: item.horseVehicleCode,
        trailer1VehicleId: item.trailer1VehicleId,
        trailer1VehicleCode: item.trailer1VehicleCode,
        trailer2VehicleId: item.trailer2VehicleId,
        trailer2VehicleCode: item.trailer2VehicleCode,
        driverTypeId: 0,
        driverType: '',
        driverId: 0,
        driverNickname: '',
        from: item.from, // this might need to come from the Load.from
        to: item.to, // this might need to come from the Load.to
        rate: 0,
        distance: 0,
        totalM3: 0,
        value: 0,
        linkBonus: false,
        isPrimary: true,
        criticalComment: '',
      }

      this.selectedDriver = Object.assign({}, this.$store.getters['drivers/emptyDriver']);
      this.selectDriver();

      this.valid = false;
      this.title = 'Create New Payment for Load #' + item.loadId;

      if (this.$refs.itemForm) {
        (this.$refs.itemForm as any).resetValidation();
      }

      this.showItemDialog = true;
    }

    launchUpdateItemDialog(item: DriverPayment) {
      this.clickedItem = Object.assign({}, item);

      this.selectedDriver = this.$store.getters['drivers/activeDrivers'].find((driver: Driver) => driver.id === item.driverId);

      this.title = 'Update Payment to ' + (this.clickedItem.driverNickname) + ' for Load #' + item.loadId;
      if (this.$refs.itemForm) {
        (this.$refs.itemForm as any).resetValidation();
      }

      this.showItemDialog = true;
    }

    doCreateUpdate() {
      if (this.clickedItem.id <= 0) {
        this.doCreate();
      } else {
        this.doUpdate();
      }
    }

    async doCreate() {
      try {
        this.clicked = true;

        const response = await DriverPaymentService.create({
          loadId: this.clickedItem.loadId,
          driverId: this.selectedDriver.id,
          from: this.clickedItem.from,
          to: this.clickedItem.to,
          distance: this.clickedItem.distance,
        });

        if (response && response.data) {
          if (response.data.result && response.data.result === 'false') {
            AppService.errorHandler(response.data.message);
          } else {
            AppService.successHandler(response.data.message);
            await this.listPending(true);
            this.paymentFilter = 'Pending';
            this.filterPayments();
            this.showItemDialog = false;
          }
        } else {
          // response is undefined or has no data field - SHOULD NEVER HAPPEN!
          throw new Error('response: ' + JSON.stringify(response));
        }

      } catch (error) {
        if (error.response) {
          AppService.errorHandler(error.response.statusText);
        } else {
          AppService.logSupportDebug('DriverPayments.vue - doCreate - 1396 - ' + error);
          AppService.errorHandler(this.$store.getters['app/messages'].couldNotConnect);
        }

      } finally {
        this.clicked = false;
      }
    }

    async doUpdate() {
      try {
        this.clicked = true;

        const response = await DriverPaymentService.update({
          id: this.clickedItem.id,
          driverId: this.selectedDriver.id,
          from: this.clickedItem.from,
          to: this.clickedItem.to,
          distance: this.clickedItem.distance,
        });

        if (response && response.data) {
          if (response.data.result && response.data.result === 'false') {
            AppService.errorHandler(response.data.message);
          } else {
            AppService.successHandler(response.data.message);
            await this.listPending(true);
            this.filterPayments();
            this.showItemDialog = false;
          }
        } else {
          // response is undefined or has no data field - SHOULD NEVER HAPPEN!
          throw new Error('response: ' + JSON.stringify(response));
        }

      } catch (error) {
        if (error.response) {
          AppService.errorHandler(error.response.statusText);
        } else {
          AppService.logSupportDebug('DriverPayments.vue - doUpdate - 1435 - ' + error);
          AppService.errorHandler(this.$store.getters['app/messages'].couldNotConnect);
        }

      } finally {
        this.clicked = false;
      }
    }
  // #endregion

  // #region Functions required for the CSV Download 
    getDataForCSV() {
      return this.displayPayments.reduce((a: any, b: any) => {
        let newItem = b;
        newItem = Object.assign({}, b, { linkBonus: b.linkBonus ? 'R100' : 'R0', isPrimary: b.isPrimary ? '' : '[+]' });

        return a.concat(newItem);
      }, []);
    }

    getFieldsForCSV() {
      return {
        'Load No': 'loadId',
        'Additional': 'isPrimary',
        'Date': 'loadDate',
        'Type': 'driverType',
        'Driver': 'driverNickname',
        'Truck': 'horseVehicleCode',
        'Trailer 1': 'trailer1VehicleCode',
        'Trailer 2': 'trailer2VehicleCode',
        'From': 'from',
        'To': 'to',
        'Distance': 'distance',
        'Rate': 'rate',
        'Total M3': 'totalM3',        
        'Value': 'value',
        'Link Bonus': 'linkBonus',
        'Approved At': 'approvedAt',
        'Approved By': 'approvedBy',
        'Finalised At': 'finalisedAt',
        'Finalised By': 'finalisedBy',
      };
    }
  // #endregion

  // #region Functions required for the List Load Events / Add Comment Dialog
    async listLoadEvents() {
      try {
        // always check for all the comments in case someone else added one in the background - until we are using websockets
        const response = await LoadEventService.listByLoad({params: {loadId: this.clickedItem.loadId}});

        if (response && response.data) {
          if (response.data.result && response.data.result === 'false') {
            this.loadEvents = [];
          } else {
           this.loadEvents = response.data;
          }
        } else {
          // response is undefined or has no data field - SHOULD NEVER HAPPEN!
          throw new Error('response: ' + JSON.stringify(response));
        }

      } catch (error) {
        if (error.response) {
          AppService.errorHandler(error.response.statusText);
        } else {
          AppService.logSupportDebug('DriverPayments.vue - listLoadEvents - 1501 - ' + error);
          AppService.errorHandler(this.$store.getters['app/messages'].couldNotConnect);
        }

      }
    }

    async launchLoadEventDialog(item: DriverPayment) {
      this.clickedItem = Object.assign({}, item);
      this.newComment = '';
      
      // fetch load events associated with this load
      await this.listLoadEvents();

      this.commentFormValid = false;
      this.loadEventDialogTitle = 'Events for Load #' + this.clickedItem.loadId;

      if (this.$refs.commentForm) {
        (this.$refs.commentForm as any).resetValidation();
      }

      this.showLoadEventDialog = true;
    }

    async createComment() {
      try {
        this.addCommentClicked = true;

        const response = await LoadEventService.createComment({
          loadId: this.clickedItem.loadId,
          comment: this.newComment,
          isCritical: false
        });

        if (response && response.data) {
          if (response.data.result && response.data.result === 'false') {
            AppService.errorHandler(response.data.message);
          } else {
            AppService.successHandler(response.data.message);
            this.showLoadEventDialog = false;
          }
        } else {
          // response is undefined or has no data field - SHOULD NEVER HAPPEN!
          throw new Error('response: ' + JSON.stringify(response));
        }

      } catch (error) {
        if (error.response) {
          AppService.errorHandler(error.response.statusText);
        } else {
          AppService.logSupportDebug('DriverPayments.vue - createComment - 1551 - ' + error);
          AppService.errorHandler(this.$store.getters['app/messages'].couldNotConnect);
        }

      } finally {
        this.addCommentClicked = false;
      }
    }
  // #endregion

  // #region Functions required for Bulk Delete
    async activateBulkDelete() {
      this.isBulkDelete = true;
      this.selectedPayments = [];
    }

  // #endregion

  // #region Functions required for the Confirm Action Dialog for Delete/Approve
    async launchDeleteItemDialog(item: DriverPayment) {
      this.clickedItem = Object.assign({}, item);

      this.action = 'DELETE';
      this.dialogContent = 'You are about to <span style="font-weight: 700; color: #C01212">delete</span> ' 
        + 'the Driver Payment for ' + this.clickedItem.driverNickname + ' on Load #' + this.clickedItem.loadId;
      this.showConfirmActionDialog = true;
    }

    async launchBulkDeleteDialog() {
      this.action = 'DELETE';
      this.dialogContent = 'You are about to <span style="font-weight: 700; color: #C01212">delete</span> ' 
        + this.selectedPayments.length + ' Driver Payment' + (this.selectedPayments.length > 1 ? 's' : '') + '.';
      this.showConfirmActionDialog = true;
    }

    async launchApproveItemDialog(item: DriverPayment) {
      this.clickedItem = Object.assign({}, item);

      this.action = 'APPROVE';
      this.valid = true;      this.dialogContent = 'You are about to <span style="font-weight: 700; color: green">approve</span> ' 
        + 'the Driver Payment for ' + this.clickedItem.driverNickname + ' on Load #' + this.clickedItem.loadId;
      this.showConfirmActionDialog = true;
    }

    async launchFinaliseItemDialog(item: DriverPayment) {
      this.selectedPayments = [];
      this.clickedItem = Object.assign({}, item);

      this.action = 'FINALISE';
      this.dialogContent = 'You are about to <span style="font-weight: 700; color: #00a4e3">finalise</span> ' 
        + 'the Driver Payment for ' + this.clickedItem.driverNickname + ' on Load #' + this.clickedItem.loadId;
      this.showConfirmActionDialog = true;
    }

    async launchFinaliseBatchDialog() {
      this.action = 'FINALISE';
      this.dialogContent = 'You are about to <span style="font-weight: 700; color: #00a4e3">finalise</span> ' 
        + this.selectedPayments.length + ' Driver Payment' + (this.selectedPayments.length > 1 ? 's' : '') + '.';
      this.showConfirmActionDialog = true;
    }

    ConfirmActionDialog_cancel() {
      this.showConfirmActionDialog = false;
    }

    async ConfirmActionDialog_doConfirmed() {
      if (this.action === 'DELETE') {
        await this.doDelete();
      } else if (this.action === 'APPROVE') {
        await this.doApprove();
      } else if (this.action === 'FINALISE') {
        await this.doFinalise();       
      }
    }

    async doDelete() {
      try {
        this.confirmActionDelay = true;

        let paymentIds: string = '';
        if (this.selectedPayments.length > 0) {
          paymentIds = this.selectedPayments.map((item: DriverPayment) => item.id).join(',');
        } else {
          paymentIds = '' + this.clickedItem.id;
        }

        const response = await DriverPaymentService.delete({
          ids: paymentIds,
        });

        if (response && response.data) {
          if (response.data.result && response.data.result === 'false') {
            AppService.errorHandler(response.data.message);
          } else {
            AppService.successHandler(response.data.message);
            await this.listPending(true);
            this.filterPayments();
            this.showConfirmActionDialog = false;
            this.isBulkDelete = false;
          }
        } else {
          // response is undefined or has no data field - SHOULD NEVER HAPPEN!
          throw new Error('response: ' + JSON.stringify(response));
        }

      } catch (error) {
        if (error.response) {
          AppService.errorHandler(error.response.statusText);
        } else {
          AppService.logSupportDebug('DriverPayments.vue - doDelete - 1660 - ' + error);
          AppService.errorHandler(this.$store.getters['app/messages'].couldNotConnect);
        }

      } finally {
        this.confirmActionDelay = false;
        this.selectedPayments = [];
      }
    }

    async doApprove() {
      try {
        this.confirmActionDelay = true;

        const response = await DriverPaymentService.approve({
          id: this.clickedItem.id,
        });

        if (response && response.data) {
          if (response.data.result && response.data.result === 'false') {
            AppService.errorHandler(response.data.message);
          } else {
            AppService.successHandler(response.data.message);
            await this.listPending(true); // item will no longer be listed here
            await this.listApproved(true); // item will now be in this list
            this.filterPayments();
            this.showConfirmActionDialog = false;
          }
        } else {
          // response is undefined or has no data field - SHOULD NEVER HAPPEN!
          throw new Error('response: ' + JSON.stringify(response));
        }

      } catch (error) {
        if (error.response) {
          AppService.errorHandler(error.response.statusText);
        } else {
          AppService.logSupportDebug('DriverPayments.vue - doApprove - 1697 - ' + error);
          AppService.errorHandler(this.$store.getters['app/messages'].couldNotConnect);
        }

      } finally {
        this.confirmActionDelay = false;
      }
    }

    async doFinalise() {
      try {
        this.confirmActionDelay = true;

        let paymentIds: string = '';
        if (this.selectedPayments.length > 0) {
          paymentIds = this.selectedPayments.map((item: DriverPayment) => item.id).join(',');
        } else {
          paymentIds = '' + this.clickedItem.id;
        }

        const response = await DriverPaymentService.finalise({
          ids: paymentIds,
        });

        if (response && response.data) {
          if (response.data.result && response.data.result === 'false') {
            AppService.errorHandler(response.data.message);
          } else {
            AppService.successHandler(response.data.message);
            await this.listApproved(true);
            this.filterPayments();
            this.showConfirmActionDialog = false;
          }
        } else {
          // response is undefined or has no data field - SHOULD NEVER HAPPEN!
          throw new Error('response: ' + JSON.stringify(response));
        }
      } catch (error) {
        if (error.response) {
          AppService.errorHandler(error.response.statusText);
        } else {
          AppService.logSupportDebug('DriverPayments.vue - doFinalise - 1738 - ' + error);
          AppService.errorHandler(this.$store.getters['app/messages'].couldNotConnect);
        }

      } finally {
        this.confirmActionDelay = false;
        this.selectedPayments = [];
      }
    }
  // #endregion
}
